import { useTooltip } from "./useTooltip.js";
import { TooltipContext, useTooltipContext } from "./TooltipContext.js";
import Tooltip_module_default from "./Tooltip.module.js";
import classNames from "classnames";
import { cloneElement, isValidElement, useMemo } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { FloatingArrow, FloatingPortal, useMergeRefs } from "@floating-ui/react";
//#region src/components/Tooltip/Tooltip.tsx
var hasLabel = (props) => "label" in props && !!props.label;
/**
* A tooltip component
*/
function Tooltip({ children, isTriggerInteractive = true, nonInteractiveTriggerTabIndex = 0, ...props }) {
	const context = useTooltip({
		isTriggerInteractive,
		...props
	});
	return /* @__PURE__ */ jsxs(TooltipContext.Provider, {
		value: context,
		children: [/* @__PURE__ */ jsx(TooltipAnchor, {
			isTriggerInteractive,
			nonInteractiveTriggerTabIndex,
			children
		}), /* @__PURE__ */ jsxs(TooltipContent, { children: [/* @__PURE__ */ jsx("span", {
			id: context.labelId,
			children: hasLabel(props) ? props.label : props.description
		}), /* @__PURE__ */ jsx(Caption, {})] })]
	});
}
function Caption() {
	const { caption, captionId } = useTooltipContext();
	if (!caption) return null;
	/**
	* Forcing dark theme, so that we have the correct contrast when
	* using the text color secondary on a solid dark background.
	* This is temporary and should only remain until we figure out
	* the approach to on-solid tokens
	**/
	return /* @__PURE__ */ jsx(typeof caption === "string" ? "span" : "div", {
		id: captionId,
		className: classNames(Tooltip_module_default.caption, "cpd-theme-dark"),
		children: caption
	});
}
/**
* The content of the tooltip
* @param children
*/
function TooltipContent({ children }) {
	const { context: floatingContext, open, arrowRef, purpose, ...rest } = useTooltipContext();
	if (!open && purpose !== "label") return null;
	return /* @__PURE__ */ jsx(FloatingPortal, { children: /* @__PURE__ */ jsxs("div", {
		ref: rest.refs.setFloating,
		style: rest.floatingStyles,
		...rest.tooltipProps,
		...rest.getFloatingProps(),
		className: classNames(Tooltip_module_default.tooltip, { [Tooltip_module_default.invisible]: purpose === "label" && !open }),
		children: [/* @__PURE__ */ jsx(FloatingArrow, {
			ref: arrowRef,
			context: floatingContext,
			width: 10,
			height: 6,
			className: Tooltip_module_default.arrow
		}), children]
	}) });
}
/**
* The anchor of the tooltip
* @param children
*/
var TooltipAnchor = ({ children, isTriggerInteractive, nonInteractiveTriggerTabIndex }) => {
	const context = useTooltipContext();
	const childrenRef = children?.ref;
	const ref = useMergeRefs([context.refs.setReference, childrenRef]);
	const element = useMemo(() => {
		if (!isValidElement(children)) return;
		if (isTriggerInteractive) return cloneElement(children, context.getReferenceProps({
			...typeof children.props === "object" ? children.props : {},
			ref
		}));
		else {
			const { "aria-labelledby": labelId, "aria-describedby": descriptionId, ...spanProps } = context.getReferenceProps({
				ref,
				tabIndex: nonInteractiveTriggerTabIndex
			});
			return /* @__PURE__ */ jsx("span", {
				tabIndex: nonInteractiveTriggerTabIndex,
				...spanProps,
				children: cloneElement(children, {
					"aria-labelledby": labelId,
					"aria-describedby": descriptionId
				})
			});
		}
	}, [
		context,
		ref,
		children
	]);
	if (!element) throw new Error("Tooltip anchor must be a single valid React element");
	return element;
};
//#endregion
export { Tooltip };

//# sourceMappingURL=Tooltip.js.map